/**
 * The stage-colour palette - ONE list, shared by every surface that offers a
 * colour.
 *
 * Locked at DEC-058 as sixteen curated lighting colours, and it has lived
 * inside `TimelineEditor.tsx` since. Lifted here 2026-09-17 because the
 * lighting control items are the second surface to offer colours, and two
 * copies of a vocabulary is precisely how this project produced
 * `--sk-weight: 500` in the app and `700` in the workbench for a whole arc.
 * The Timeline now imports from here; the constant is not duplicated.
 *
 * WHY A FIXED LIST AND NOT A COLOUR WHEEL. The same reasoning DEC-127 records
 * for the item-colour palette, and the operator's own words there: stage
 * lighting is thought about in named colours, not in hex. On an iPad a row of
 * tappable swatches is a tap target; a wheel is a fiddle. The ceiling for
 * anyone who genuinely wants an arbitrary colour is a future Advanced
 * affordance, not a reason to make the common case worse.
 *
 * STORAGE IS 0..1 PER CHANNEL, because that is what `ColorMarkerStep` has
 * always stored and changing it would rewrite authored timelines. OPBridge's
 * drive routes take 0-255, so the conversion happens at the wire edge
 * (`rgb255`), once, rather than the two representations both being "the"
 * palette.
 */

export interface StageColor {
  label: string;
  /** 0..1 per channel. */
  rgb: { r: number; g: number; b: number };
}

/** DEC-058's sixteen. Order is the authored one and is load-bearing for the
 *  8-wide x 2-row grid both surfaces render: Off and the two whites lead,
 *  then the wheel walks red -> pink. Do not re-sort. */
export const STAGE_COLORS: StageColor[] = [
  { label: 'Off',        rgb: { r: 0.00, g: 0.00, b: 0.00 } },
  { label: 'White',      rgb: { r: 1.00, g: 1.00, b: 1.00 } },
  { label: 'Cool White', rgb: { r: 0.81, g: 0.90, b: 1.00 } },
  { label: 'Red',        rgb: { r: 1.00, g: 0.00, b: 0.00 } },
  { label: 'Deep Red',   rgb: { r: 0.55, g: 0.00, b: 0.00 } },
  { label: 'Amber',      rgb: { r: 1.00, g: 0.69, b: 0.00 } },
  { label: 'Yellow',     rgb: { r: 1.00, g: 1.00, b: 0.00 } },
  { label: 'Green',      rgb: { r: 0.00, g: 1.00, b: 0.00 } },
  { label: 'Teal',       rgb: { r: 0.00, g: 0.80, b: 0.80 } },
  { label: 'Cyan',       rgb: { r: 0.00, g: 1.00, b: 1.00 } },
  { label: 'Sky Blue',   rgb: { r: 0.40, g: 0.75, b: 1.00 } },
  { label: 'Blue',       rgb: { r: 0.00, g: 0.20, b: 1.00 } },
  { label: 'Deep Blue',  rgb: { r: 0.00, g: 0.00, b: 0.60 } },
  { label: 'Purple',     rgb: { r: 0.50, g: 0.00, b: 1.00 } },
  { label: 'Magenta',    rgb: { r: 1.00, g: 0.00, b: 1.00 } },
  { label: 'Pink',       rgb: { r: 1.00, g: 0.55, b: 0.75 } },
];

/** The swatch a new colour control starts on. White, not Off: a control that
 *  does nothing when you first tap it teaches the operator that lighting
 *  controls do nothing. */
export const DEFAULT_STAGE_COLOR = 'White';

export function stageColorByLabel(label: string): StageColor | undefined {
  return STAGE_COLORS.find((c) => c.label === label);
}

/** `#rrggbb` for CSS. Clamps, so a hand-edited value out of range still
 *  renders something rather than producing `#NaNNaNNaN`. */
export function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const to = (v: number) => {
    const n = Math.max(0, Math.min(255, Math.round(v * 255)));
    return n.toString(16).padStart(2, '0');
  };
  return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

/** 0..1 triple -> the 0-255 bytes OPBridge's drive routes take. The single
 *  conversion point between the authored representation and the wire. */
export function rgb255(rgb: { r: number; g: number; b: number }): { r: number; g: number; b: number } {
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return { r: to(rgb.r), g: to(rgb.g), b: to(rgb.b) };
}

/**
 * Perceived luminance, 0..1, for deciding whether text on a swatch should be
 * black or white.
 *
 * Rec. 709 coefficients rather than a flat average, because a flat average
 * calls pure green (0,1,0) mid-grey and puts white text on the brightest
 * swatch in the palette. Threshold is 0.55 rather than 0.5 to bias toward
 * dark text, which stays legible on the ambiguous middles (Amber, Teal).
 *
 * This is the "state must be LUMINANCE not hue" lesson applied to a label
 * rather than to a state: a swatch's readability is a brightness question.
 */
export function luminance(rgb: { r: number; g: number; b: number }): number {
  return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
}

export function onColorText(rgb: { r: number; g: number; b: number }): string {
  return luminance(rgb) > 0.55 ? '#000' : '#fff';
}
